import axios from 'axios';

import { API_BASE_URL as API_URL } from '@/config/apiBaseUrl';

// Create axios instance with proper config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export interface HeatStockFilters {
  item_id?: string;
  warehouse_id?: string;
  heat_number?: string;
  critical_only?: boolean;
  include_zero?: boolean;
  mtc_status?: 'all' | 'with' | 'missing';
}

export interface HeatReceiptSource {
  source_type: 'GRN' | 'OpeningStock' | 'StockAdjustment';
  source_id: string;
  source_number: string | null;
  source_date: string | null;
  supplier_name: string | null;
  po_id: string | null;
  po_number: string | null;
}

export interface HeatStockRow {
  item_id: string;
  item_code: string;
  item_name: string;
  category_name: string | null;
  is_critical: boolean;
  heat_number_mandatory: boolean;
  warehouse_id: string;
  warehouse_code: string | null;
  warehouse_name: string | null;
  warehouse_type: string | null;
  location_id: string;
  location_code: string | null;
  location_name: string | null;
  heat_number: string;
  dimension: string;
  on_hand_quantity: number;
  reserved_quantity: number;
  free_quantity: number;
  uom: string;
  average_cost: number;
  total_value: number;
  ownership_type: string;
  owner_name: string | null;
  last_transaction_date: string | null;
  mtc_document_id: string | null;
  mtc_document_number: string | null;
  mtc_reference_number: string | null;
  source: HeatReceiptSource | null;
  heat_missing: boolean;
  mtc_missing: boolean;
}

export interface HeatLedgerRow {
  id: string;
  item_id: string;
  warehouse_id: string;
  heat_number: string;
  dimension: string;
  transaction_date: string;
  transaction_time: string;
  transaction_type: string;
  voucher_type: string | null;
  voucher_number: string | null;
  voucher_id: string | null;
  quantity: number | string;
  uom: string;
  balance_qty: number | string;
  project_id: string | null;
  project_number?: string | null;
  project_name?: string | null;
  remarks?: string | null;
  warehouse?: { id: string; warehouse_code?: string; warehouse_name?: string } | null;
}

export interface Item {
  id: string;
  company_id: string;
  item_code: string;
  item_name: string;
  item_description?: string;
  rack_number?: string | null;
  category_id?: string;
  item_type: string; // Raw Material, Semi-Finished, Finished Good, Consumable, Service
  base_uom?: string; // Unit of Measure: PCS, KG, M, SQM, etc.
  purchase_uom?: string;
  sales_uom?: string;
  purchase_uom_conversion?: number;
  sales_uom_conversion?: number;
  
  // Stock Control
  stock_type?: string;
  track_serial_number: boolean;
  track_batch_number: boolean;
  track_expiry?: boolean;
  reorder_level: number;
  reorder_quantity: number;
  minimum_stock_level: number;
  maximum_stock_level: number;
  safety_stock?: number;
  lead_time_days?: number;
  
  // Costing
  valuation_method: string; // FIFO, Moving Average, Standard Cost
  standard_cost: number;
  average_cost: number;
  last_purchase_price: number;
  standard_selling_price: number;
  
  // Tax
  vat_applicable: boolean;
  vat_percentage: number;
  
  // Additional Info
  manufacturer?: string;
  manufacturer_part_number?: string;
  /** Material / profile (import columns profile_grade, profile_heat_no, profile_dimension, profile_length) */
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
  /** LENGTH | LENGTH_WIDTH | LENGTH_WIDTH_HEIGHT; null = quantity only */
  measurement_type?: string | null;
  default_width_mm?: number | string | null;
  default_height_mm?: number | string | null;
  barcode?: string;
  hs_code?: string;
  shelf_life_days?: number;
  weight?: number;
  weight_uom?: string;
  specifications?: string;
  image_url?: string;
  is_active: boolean;
  /** Master-data flag: critical vs non-critical item */
  is_critical?: boolean;
  /** When critical: require heat number on GRN / stock moves */
  heat_number_mandatory?: boolean;
  /** When critical: require MTC document per heat line on GRN */
  mtc_required_on_receipt?: boolean;
  /** GRN posting can create fixed-asset register rows */
  is_fixed_asset?: boolean;
  inventory_account_id?: string | null;
  expense_account_id?: string | null;
  revenue_account_id?: string | null;
  is_purchasable?: boolean;
  is_saleable?: boolean;
  is_discontinued?: boolean;
  item_group?: string;
  brand?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface ItemCategory {
  id: string;
  company_id: string;
  category_code: string;
  category_name: string;
  parent_category_id?: string;
  description?: string;
  is_active: boolean;
}

export interface UomUnit {
  id: string;
  company_id: string;
  code: string;
  name: string;
  description?: string | null;
  category?: string | null;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ItemWithStock {
  id: string;
  item_code: string;
  item_name: string;
  base_uom?: string;
  category_name?: string | null;
  reorder_level: number;
  minimum_stock_level?: number;
  maximum_stock_level?: number;
  is_critical?: boolean;
  available_qty: number;
  reserved_qty?: number;
  /** Free = max(0, available − reserved); prefer this for issue/ATP decisions */
  free_quantity?: number;
  current_stock: number;
  average_cost: number;
  /** Same as average_cost — weighted unit cost from stock value */
  unit_price: number;
  total_value: number;
  /** Same as total_value — stock amount */
  amount: number;
  warehouse_id?: string;
  warehouse_name?: string;
}

/** Normalize stock API rows (clean DTO or legacy TypeORM raw aliases). */
function normalizeItemWithStock(raw: unknown): ItemWithStock {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const id = String(row.id ?? row.item_id ?? '').trim();
  const availableQty =
    Number(row.available_qty ?? row.current_stock ?? 0) || 0;
  const totalValue = Number(row.total_value ?? row.amount ?? 0) || 0;
  const unitPriceRaw = Number(row.unit_price ?? row.average_cost ?? 0);
  const unitPrice =
    Number.isFinite(unitPriceRaw) && unitPriceRaw > 0
      ? unitPriceRaw
      : availableQty > 0
        ? totalValue / availableQty
        : 0;

  return {
    id,
    item_code: String(row.item_code ?? row.item_item_code ?? ''),
    item_name: String(row.item_name ?? row.item_item_name ?? ''),
    base_uom: String(row.base_uom ?? row.item_base_uom ?? ''),
    category_name:
      row.category_name != null
        ? String(row.category_name)
        : row.category_category_name != null
          ? String(row.category_category_name)
          : null,
    reorder_level: Number(row.reorder_level ?? row.item_reorder_level ?? 0) || 0,
    minimum_stock_level:
      Number(row.minimum_stock_level ?? row.item_minimum_stock_level ?? 0) || 0,
    maximum_stock_level:
      Number(row.maximum_stock_level ?? row.item_maximum_stock_level ?? 0) || 0,
    is_critical: Boolean(row.is_critical ?? row.item_is_critical),
    available_qty: availableQty,
    reserved_qty: Number(row.reserved_qty ?? 0) || 0,
    free_quantity:
      row.free_quantity != null
        ? Math.max(0, Number(row.free_quantity) || 0)
        : Math.max(0, availableQty - (Number(row.reserved_qty ?? 0) || 0)),
    current_stock: availableQty,
    average_cost: unitPrice,
    unit_price: unitPrice,
    total_value: totalValue,
    amount: totalValue,
    warehouse_id: row.warehouse_id != null ? String(row.warehouse_id) : undefined,
    warehouse_name: row.warehouse_name != null ? String(row.warehouse_name) : undefined,
  };
}

export interface StockMovementRow {
  id: string;
  transaction_date: string | null;
  transaction_time: string | null;
  transaction_type: string;
  item_id: string;
  item_code: string;
  item_name: string;
  quantity: number;
  uom: string;
  unit_price: number;
  amount: number;
  warehouse_id: string;
  warehouse_name: string;
  warehouse_code: string;
  from_location: string;
  to_location: string;
  voucher_type: string | null;
  voucher_number: string | null;
  voucher_id: string | null;
  remarks: string | null;
  user_name: string | null;
  heat_number?: string;
  dimension?: string;
}

export class InventoryService {
  async getAllItems(filters?: {
    category_id?: string;
    item_type?: string;
    is_active?: boolean;
    search?: string;
    /** When set, backend caps rows (pickers should pass this to avoid full-catalog timeouts). */
    limit?: number;
  }): Promise<Item[]> {
    try {
      console.log('[InventoryService] getAllItems - filters:', filters);
      const response = await apiClient.get('/inventory/items', { params: filters });
      console.log('[InventoryService] getAllItems - response:', response.data);

      // Handle different response structures
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (response.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      console.warn('[InventoryService] Unexpected response structure:', response.data);
      return [];
    } catch (error: any) {
      console.error('[InventoryService] Error fetching items:', error);
      console.error('[InventoryService] Error response:', error.response?.data);
      // Re-throw so React Query can surface loading/error instead of a silent empty picker.
      throw error;
    }
  }

  async getItemById(id: string): Promise<Item> {
    const response = await apiClient.get(`/inventory/items/${id}`);
    return response.data.data;
  }

  async getItemsWithStock(warehouse_id?: string): Promise<ItemWithStock[]> {
    const response = await apiClient.get('/inventory/items/stock', {
      params: warehouse_id ? { warehouse_id } : undefined
    });
    const raw = response.data?.data;
    const rows: unknown[] = Array.isArray(raw) ? raw : [];
    return rows.map((row) => normalizeItemWithStock(row));
  }

  async getLowStockItems(): Promise<ItemWithStock[]> {
    const response = await apiClient.get('/inventory/items/low-stock');
    return response.data.data;
  }

  async getStockMovements(filters?: {
    warehouse_id?: string;
    transaction_type?: string;
    from_date?: string;
    to_date?: string;
    search?: string;
    limit?: number;
  }): Promise<StockMovementRow[]> {
    const response = await apiClient.get('/inventory/stock-movements', { params: filters });
    const raw = response.data?.data;
    return Array.isArray(raw) ? (raw as StockMovementRow[]) : [];
  }

  async getHeatWiseStock(filters?: HeatStockFilters): Promise<HeatStockRow[]> {
    const params: Record<string, string> = {};
    if (filters?.item_id) params.item_id = filters.item_id;
    if (filters?.warehouse_id) params.warehouse_id = filters.warehouse_id;
    if (filters?.heat_number?.trim()) params.heat_number = filters.heat_number.trim();
    if (filters?.critical_only) params.critical_only = '1';
    if (filters?.include_zero) params.include_zero = '1';
    if (filters?.mtc_status && filters.mtc_status !== 'all') params.mtc_status = filters.mtc_status;
    const response = await apiClient.get('/inventory/stock/heat-wise', { params });
    const raw = response.data?.data;
    return Array.isArray(raw) ? (raw as HeatStockRow[]) : [];
  }

  async getHeatLedger(filters: { item_id?: string; heat_number?: string; dimension?: string }): Promise<HeatLedgerRow[]> {
    const response = await apiClient.get('/inventory/items/traceability/ledger', { params: filters });
    const raw = response.data?.data;
    return Array.isArray(raw) ? (raw as HeatLedgerRow[]) : [];
  }

  async createItem(data: Partial<Item>): Promise<Item> {
    const response = await apiClient.post('/inventory/items', data);
    return response.data.data;
  }

  /** Lean create for BOQ / estimation (+ optional opening / initial stock). */
  async quickCreateItem(data: {
    item_name: string;
    base_uom: string;
    item_code?: string;
    standard_cost?: number;
    item_type?: string;
    is_critical?: boolean;
    heat_number_mandatory?: boolean;
    mtc_required_on_receipt?: boolean;
    initial_stock?: {
      warehouse_id: string;
      quantity: number;
      unit_rate?: number;
      heat_number?: string;
      dimension?: string;
    };
  }): Promise<{ item: Item; stock_posted: boolean }> {
    const response = await apiClient.post('/inventory/items/quick-create', data);
    return response.data.data;
  }

  async updateItem(id: string, data: Partial<Item>): Promise<Item> {
    const response = await apiClient.put(`/inventory/items/${id}`, data);
    return response.data.data;
  }

  async deleteItem(id: string): Promise<void> {
    await apiClient.delete(`/inventory/items/${id}`);
  }

  // Categories
  async getAllCategories(): Promise<ItemCategory[]> {
    const response = await apiClient.get('/inventory/categories');
    return response.data.data;
  }

  async getCategoryTree(): Promise<ItemCategory[]> {
    const response = await apiClient.get('/inventory/categories/tree');
    return response.data.data;
  }

  async createCategory(data: Partial<ItemCategory>): Promise<ItemCategory> {
    const response = await apiClient.post('/inventory/categories', data);
    return response.data.data;
  }

  async updateCategory(id: string, data: Partial<ItemCategory>): Promise<ItemCategory> {
    const response = await apiClient.put(`/inventory/categories/${id}`, data);
    return response.data.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/inventory/categories/${id}`);
  }

  // UOM master (Supply Chain)
  async getUomUnits(options?: { all?: boolean }): Promise<UomUnit[]> {
    const response = await apiClient.get('/inventory/uom-units', {
      params: options?.all ? { all: '1' } : undefined,
    });
    return response.data?.data ?? [];
  }

  async createUomUnit(data: Partial<UomUnit>): Promise<UomUnit> {
    const response = await apiClient.post('/inventory/uom-units', data);
    return response.data?.data;
  }

  async updateUomUnit(id: string, data: Partial<UomUnit>): Promise<UomUnit> {
    const response = await apiClient.put(`/inventory/uom-units/${id}`, data);
    return response.data?.data;
  }

  async deleteUomUnit(id: string): Promise<void> {
    await apiClient.delete(`/inventory/uom-units/${id}`);
  }
}

export default new InventoryService();
